import { motion } from 'framer-motion'
import { styled, css } from 'styled-components'
import { SwiperSlide } from 'swiper/react'

import { IconPlay as IconPlayBase } from 'components/icons/play'
import { mq } from 'styles/mq'
import { typography } from 'styles/typography'
import { resetButtonStyle } from 'styles/utils'

const iconSize = css`
  width: 40px;
  height: 40px;
  ${mq.medium} {
    width: 56px;
    height: 56px;
  }
  ${mq.large} {
    width: 72px;
    height: 72px;
  }
`

export const Container = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-top: 1px solid var(--border-color);
`

export const SwiperContainer = styled.div<{
  $slidePerView: boolean
  $type: string
}>`
  position: relative;
  margin: 1.6rem auto;
  max-width: 100%;
  width: 77.5rem;
  ${props => (props.$type === 'image' ? 'width: 75rem;' : 'width: 77.5rem;')}
  padding-inline: 3.6rem;

  ${mq.small} {
    margin: 2rem auto;
  }

  ${mq.medium} {
    margin: 3.2rem auto;
    width: 90rem;
    ${props => (props.$type === 'image' ? 'width: 75rem;' : 'width: 90rem;')}
    padding-inline: 6rem;
  }

  @media (min-width: 1023px) {
    padding-inline: 3rem;
  }

  ${mq.large} {
    padding-inline: 3rem;
    margin: 2rem auto;
    width: 77.5rem;
    width: 96rem;
    ${props => (props.$type === 'image' ? 'width: 75rem;' : 'width: 96rem;')}
  }

  .swiper {
    overflow: visible;
    ${props => props.$type === 'image' && 'aspect-ratio: 16/9'};
    ${props => props.$type === 'image' && 'height: auto;'};
    ${mq.medium} {
      ${props =>
        props.$type === 'image' && 'height: 462px; aspect-ratio: auto;'};
    }
    ${mq.large} {
      ${props => props.$type === 'image' && 'height: 450px;'};
    }
  }

  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    transition: opacity 0.5s;
    width: ${props => (props.$slidePerView ? 'auto' : '100%')};
    padding-inline: ${props => (props.$type === 'video' ? '.3rem' : '.6rem')};

    ${mq.small} {
      padding-inline: ${props => (props.$type === 'video' ? '.4rem' : '.8rem')};
    }

    ${mq.medium} {
      padding-inline: 1rem;
    }

    ${mq.large} {
      padding-inline: ${props => (props.$type === 'video' ? '5rem' : '1.2rem')};
    }
  }
`

export const Slide = styled(SwiperSlide)<{ $isActive?: boolean }>`
  opacity: ${props => (props.$isActive ? 1 : 0.2)};
  cursor: ${props => (props.$isActive ? 'pointer' : 'auto')};
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    ${mq.large} {
      left: 5rem;
      width: calc(100% - 10rem);
    }
    height: 100%;
    border: solid 2px var(--text-color);
  }
`

export const IconPlay = styled(IconPlayBase)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2.8rem;
  color: var(--primary-color);

  ${mq.medium} {
    font-size: 3.8rem;
  }
`
export const Soon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: clamp(1.5rem, 4vw, 2.4rem);
  color: var(--primary-color);
`

export const SliderImage = styled.img<{
  $mode?: 'width' | 'height'
}>`
  width: 100%;
  height: auto;

  ${props =>
    props.$mode === 'width' &&
    css`
      width: auto;
      height: 100%;
    `}
`

export const SwiperScrollbar = styled.div`
  --swiper-scrollbar-sides-offset: 0%;

  &.swiper-scrollbar {
    position: relative;

    &.swiper-scrollbar-horizontal {
      position: relative;
      height: 1px;
      background: var(--border-color);
    }
    .swiper-scrollbar-drag {
      background: var(--primary-color);
    }
  }
`

export const SwiperNav = styled.div<{
  $align: 'left' | 'right'
  $position: string
}>`
  position: absolute;
  font-size: 4.8rem;
  color: var(--primary-color);

  ${iconSize}

  top: 50%;
  cursor: pointer;
  svg {
    width: 100%;
  }
  ${({ $align, $position }) =>
    $align === 'left'
      ? css`
          ${$position === 'near'
            ? css`
                left: -4px;
                transform: translate(0%, -50%);
                @media (min-width: 1023px) {
                  left: calc((100% - 900px) / 2);
                  transform: translate(-100%, -50%);
                }
                ${mq.large} {
                  left: calc((100% - 715px) / 2 - 72px);
                  left: calc((100% - 900px) / 2 - 72px);
                  transform: translate(50%, -50%);
                }
              `
            : css`
                left: -4px;
                transform: translateY(-50%);
                ${mq.medium} {
                  left: 12px;
                }
                @media (min-width: 1024px) {
                  left: 24px;
                }
                ${mq.large} {
                  left: 42px;
                }
              `}
        `
      : css`
          ${$position === 'near'
            ? css`
                right: -4px;
                transform: translate(0%, -50%);
                @media (min-width: 1023px) {
                  right: calc((100% - 900px) / 2);
                  transform: translate(100%, -50%);
                }
                ${mq.large} {
                  right: calc((100% - 715px) / 2 - 72px);
                  right: calc((100% - 900px) / 2 - 72px);
                  transform: translate(-50%, -50%);
                }
              `
            : css`
                right: -4px;
                transform: translateY(-50%);
                ${mq.medium} {
                  right: 12px;
                }
                @media (min-width: 1024px) {
                  right: 24px;
                }
                ${mq.large} {
                  right: 42px;
                }
              `}

          /* flip arrow for right side */
          svg {
            transform: scaleX(-1);
          }
        `}
  z-index: 2;

  &.swiper-button-disabled {
    cursor: auto;
    color: var(--grey);
    opacity: 0.5;
  }
`

export const SwiperText = styled.div`
  margin: 16px auto;
  max-width: 100%;
  /*width: 675px;*/
  width: 588px;
  ${mq.medium} {
    width: 796px;
  }
  ${mq.medium} {
    width: 746px;
  }
  padding-inline: 2rem;
  text-align: justify;

  ${mq.medium} {
    padding-inline: 3rem;
  }

  p {
    margin-block-start: 0;
  }
  a {
    color: var(--primary-color);
  }
`

/* MODAL */

export const ModalGalleryCont = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 3000;
  overflow-y: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
  .swiper {
    width: 100%;
  }
`

export const ModalInner = styled.div`
  transition: all 0.5s;
  width: 100vw;
  padding-top: 40px;
  ${mq.medium} {
    padding-top: 56px;
  }
  ${mq.large} {
    padding-top: 72px;
  }
`

export const ModalHeader = styled.div`
  position: relative;
`

export const ModalTitle = styled(motion.h2)`
  ${typography.heading.h2}
  color: var(--primary-color);
  text-align: center;
`

export const ModalSlide = styled(SwiperSlide)<{ $isActive?: boolean }>`
  opacity: ${props => (props.$isActive ? 1 : css`0 !important`)};
  text-align: center;
  img {
    width: 740px;
    max-width: 100%;
    ${mq.medium} {
      width: 100vw;
      max-height: 60vh;
      object-fit: contain;
    }
    ${mq.large} {
      width: 50vw;
    }
  }
`

export const ModalNav = styled.button<{
  $align: 'left' | 'right'
}>`
  ${resetButtonStyle}
  position: absolute;

  ${iconSize}

  color: background;
  cursor: pointer;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  ${props =>
    props.$align === 'left'
      ? css`
          left: -1px;
          display: none;
          ${mq.small} {
            left: 0px;
            display: block;
          }
          @media (min-width: 1024px) {
            left: calc((25vw - 56px) / 2);
          }
          ${mq.large} {
            left: calc((25vw - 72px) / 2);
          }
        `
      : css`
          right: -4px;
          display: none;
          ${mq.small} {
            right: 0px;
            display: block;
          }
          @media (min-width: 1024px) {
            right: calc((25vw - 56px) / 2);
          }
          ${mq.large} {
            right: calc((25vw - 72px) / 2);
          }
          transform: translateY(-50%) scaleX(-1);
        `}
  svg {
    width: 100%;
    height: 100%;
  }
  &.swiper-button-disabled {
    cursor: auto;
    color: var(--grey);
    opacity: 0.5;
  }
`
export const CloseButton = styled.div`
  position: absolute;
  bottom: 0px;
  right: -4px;
  ${mq.small} {
    right: 0px;
  }
  @media (min-width: 1024px) {
    right: calc((25vw - 56px) / 2);
  }
  ${mq.large} {
    right: calc((25vw - 72px) / 2);
  }

  ${iconSize}

  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 50%;
    height: 50%;
  }
`
export const Description = styled.div`
  color: var(--background);
  margin: 16px auto;
  width: calc(688px + 6rem);
  max-width: 100%;
  padding-inline: 2rem;
  text-align: left;
  text-align: justify;
  font-size: 1.3rem;
  ${mq.medium} {
    padding-inline: 3rem;
    font-size: 1.4rem;
  }
  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
  a {
    color: var(--primary-color);
  }
`
export const DescriptionText = styled(motion.div)`
  position: absolute;
`
